import classNames from "classnames"

import { Button, EditInPlace } from "web/components"
import { DeleteIconButton } from "web/components/IconButton"

import { usePickDateModal } from "web/hooks/modals"

import { isNonEmptyArray } from "web/services/helpers"
import { formatAs, formatAsDate } from "web/services/format"

import { ExclamationCircleIcon } from "@heroicons/react/24/outline"

export default function EditAllocations({
  subjectType,
  allocations,
  onAddAllocation,
  onRemoveAllocation,
  onUpdateAllocation,
  readOnly,
  supportPeriod = false,
}) {
  const hasAllocations = isNonEmptyArray(allocations)

  const totalAllocation = allocations.reduce(
    (sum, allocation) => sum + allocation.fraction,
    0
  )

  const allocationsValid = totalAllocation <= 1

  const hideTotal = allocations.some((a) => !!a.start_date || !!a.end_date)

  return (
    <>
      <table className="table-fixed divide-y divide-gray-200 overflow-hidden rounded-lg border-b border-gray-200 bg-white shadow">
        <thead>
          <tr className="h-11 text-neutral-6">
            <th className="w-72 p-2 text-left text-sm font-medium tracking-wider text-gray-500">
              Portfolio
            </th>
            <th className="w-36 p-2 text-center text-sm font-medium tracking-wider text-gray-500">
              Allocation
            </th>
            {supportPeriod && (
              <>
                <th className="w-48 p-2 text-center text-sm font-medium tracking-wider text-gray-500">
                  Start Date
                </th>
                <th className="w-48 p-2 text-center text-sm font-medium tracking-wider text-gray-500">
                  End Date
                </th>
              </>
            )}
            <td>&nbsp;</td>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white text-sm">
          {!hasAllocations && (
            <NoAllocationsRow
              subjectType={subjectType}
              supportPeriod={supportPeriod}
            />
          )}
          {allocations.map((allocation, index) => {
            const rowIsEven = index % 2

            return (
              <tr
                key={allocation.key}
                className={classNames("h-11 text-neutral-6", {
                  "bg-white": rowIsEven,
                  "bg-primary-11": !rowIsEven,
                })}
              >
                <td className="pl-2">
                  <a
                    href={allocation.url}
                    data-turbo-frame="_top"
                    className="text-primary-4 hover:text-feature-6"
                  >
                    <div>{allocation.label}</div>
                  </a>
                </td>

                <td className="text-center">
                  <EditInPlace
                    value={allocation.fraction}
                    format="Percent"
                    transformer={(x) => (x > 1 ? x / 100 : x)}
                    onChange={(fraction) =>
                      onUpdateAllocation(index, { fraction })
                    }
                    readOnly={readOnly}
                  />
                </td>
                {supportPeriod && (
                  <>
                    <EditAllocationDate
                      dateKey="start_date"
                      allocation={allocation}
                      index={index}
                      onUpdateAllocation={onUpdateAllocation}
                      readOnly={readOnly}
                      emptyMessage="Beginning of Time"
                    />
                    <EditAllocationDate
                      dateKey="end_date"
                      allocation={allocation}
                      index={index}
                      onUpdateAllocation={onUpdateAllocation}
                      readOnly={readOnly}
                      emptyMessage="End of Time"
                    />
                  </>
                )}
                <td className="pr-2">
                  {!readOnly && (
                    <DeleteIconButton
                      onClick={() => onRemoveAllocation(index)}
                    />
                  )}
                </td>
              </tr>
            )
          })}
          {!hideTotal && (
            <tr className="h-11 bg-primary-9 text-sm font-medium tracking-wider text-black">
              <td className="p-2">Total</td>
              <td
                className={classNames("text-center", {
                  "text-red-600": !allocationsValid,
                })}
              >
                <div className="flex items-center justify-center">
                  <span>{formatAs("Percent", { value: totalAllocation })}</span>
                  {!allocationsValid && (
                    <ExclamationCircleIcon className="ml-2 w-6" />
                  )}
                </div>
              </td>
              <td colSpan={supportPeriod ? 3 : 1} />
            </tr>
          )}
        </tbody>
      </table>
      {!readOnly && (
        <div className="mt-5">
          <Button onClick={onAddAllocation}>Add Allocation</Button>
        </div>
      )}
    </>
  )
}

function EditAllocationDate({
  dateKey,
  allocation,
  emptyMessage,
  readOnly,
  index,
  onUpdateAllocation,
}) {
  const pickAllocationDateModal = usePickDateModal()

  const date = allocation[dateKey]
  const hasDate = !!date

  async function update() {
    const newDate = await pickAllocationDateModal({
      title: "Select Date",
      date,
    })
    onUpdateAllocation(index, { [dateKey]: newDate })
  }

  const classes = classNames("text-center", {
    "cursor-pointer text-primary-4 hover:text-feature-6": !readOnly,
  })

  return (
    <td className={classes} onClick={update}>
      {hasDate ? formatAsDate({ value: date }) : emptyMessage}
    </td>
  )
}

function NoAllocationsRow({ subjectType, supportPeriod }) {
  return (
    <tr>
      <td
        className="h-11 whitespace-nowrap text-center text-sm text-gray-500"
        colSpan={supportPeriod ? 5 : 3}
      >{`This ${subjectType} has no allocations.`}</td>
    </tr>
  )
}
